// src/components/admin/NewsManagement.js
import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';

const NewsManagement = () => {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [currentItem, setCurrentItem] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [stats, setStats] = useState({ total: 0, published: 0, featured: 0, categories: 0 });

  const [formData, setFormData] = useState({
    title: '',
    category: '',
    author: '',
    excerpt: '',
    content: '',
    image_url: '',
    featured: false,
    display_order: 0,
    status: 'Published',
    published_date: ''
  });
  const [uploading, setUploading] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);

  const categories = ['Events', 'Programs', 'Member Stories', 'Partnerships', 'Milestones', 'Impact', 'Research', 'Training'];
  const statusOptions = ['Published', 'Draft', 'Archived'];

  useEffect(() => {
    fetchData();
    fetchStats();
  }, [searchTerm, filterCategory, filterStatus]);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('adminToken');

      const API_BASE = process.env.REACT_APP_API_URL;
      if (!API_BASE) {
        throw new Error('API URL not configured');
      }
      let url = `${API_BASE}/admin/news?`;
      if (searchTerm) url += `search=${encodeURIComponent(searchTerm)}&`;
      if (filterCategory) url += `category=${encodeURIComponent(filterCategory)}&`;
      if (filterStatus) url += `status=${filterStatus}&`;

      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });

      const data = await response.json();
      if (data.success) {
        setNews(data.data.news || []);
      } else {
        toast.error(data.message || 'Failed to fetch news');
      }
    } catch (err) {
      console.error('Error:', err);
      toast.error('Failed to fetch news');
    } finally {
      if (loading) setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const API_BASE = process.env.REACT_APP_API_URL;
      if (!API_BASE) {
        throw new Error('API URL not configured');
      }
      const response = await fetch(`${API_BASE}/admin/news/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) setStats(data.data);
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const handleCreate = () => {
    setModalMode('create');
    setCurrentItem(null);
    setFormData({
      title: '', category: '', author: '', excerpt: '', content: '',
      image_url: '', featured: false, display_order: 0, status: 'Published',
      published_date: new Date().toISOString().split('T')[0]
    });
    setImagePreview(null);
    setShowModal(true);
  };

  const handleEdit = (item) => {
    setModalMode('edit');
    setCurrentItem(item);
    setFormData({
      title: item.title || '',
      category: item.category || '',
      author: item.author || '',
      excerpt: item.excerpt || '',
      content: item.content || '',
      image_url: item.image_url || '',
      featured: item.featured || false,
      display_order: item.display_order || 0,
      status: item.status || 'Published',
      published_date: item.published_date ? item.published_date.substring(0, 10) : ''
    });
    // Set image preview if exists
    if (item.image_url) {
      const API_BASE = process.env.REACT_APP_BACKEND_URL;
      if (!API_BASE) {
        throw new Error('Backend URL not configured');
      }
      const imageUrl = item.image_url.startsWith('/uploads/')
        ? `${API_BASE}${item.image_url}?v=${Date.now()}`
        : item.image_url;
      setImagePreview(imageUrl);
    } else {
      setImagePreview(null);
    }
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this news article?')) return;

    try {
      const token = localStorage.getItem('adminToken');
      const API_BASE = process.env.REACT_APP_API_URL;
      if (!API_BASE) {
        throw new Error('API URL not configured');
      }
      const response = await fetch(`${API_BASE}/admin/news/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json();
      if (data.success) {
        fetchData();
        fetchStats();
        toast.success('News article deleted successfully');
      } else {
        toast.error(data.message || 'Failed to delete news article');
      }
    } catch (err) {
      toast.error('Failed to delete news article');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const token = localStorage.getItem('adminToken');
      const API_BASE = process.env.REACT_APP_API_URL;
      if (!API_BASE) {
        throw new Error('API URL not configured');
      }
      const url = modalMode === 'create'
        ? `${API_BASE}/admin/news`
        : `${API_BASE}/admin/news/${currentItem.id}`;

      const response = await fetch(url, {
        method: modalMode === 'create' ? 'POST' : 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();
      if (data.success) {
        setShowModal(false);
        fetchData();
        fetchStats();
        toast.success(`News article ${modalMode === 'create' ? 'created' : 'updated'} successfully`);
      } else {
        toast.error(data.message || 'Operation failed');
      }
    } catch (err) {
      toast.error('Operation failed');
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/') && !file.type.startsWith('video/')) {
      toast.error('Please select an image or video file');
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB');
      return;
    }

    try {
      setUploading(true);
      const token = localStorage.getItem('adminToken');
      const formDataUpload = new FormData();
      formDataUpload.append('image', file);

      const API_BASE = process.env.REACT_APP_BACKEND_URL;
      if (!API_BASE) {
        throw new Error('Backend URL not configured');
      }
      const response = await fetch(`${API_BASE}/api/admin-upload/news/image`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formDataUpload
      });

      const data = await response.json();
      if (data.success) {
        setFormData(prev => ({ ...prev, image_url: data.url }));
        setImagePreview(`${API_BASE}${data.url}?v=${Date.now()}`);
        toast.success('Image uploaded successfully');
      } else {
        toast.error(data.message || 'Upload failed');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="container-fluid py-4">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="fw-bold mb-1" style={{ color: '#6B8E23' }}>News Management</h2>
          <p className="text-muted mb-0">Manage news articles and stories</p>
        </div>
        <button className="btn text-white fw-bold" style={{ backgroundColor: '#6B8E23' }} onClick={handleCreate}>
          <i className="fas fa-plus me-2"></i>Add News Article
        </button>
      </div>

      {/* Stats Cards */}
      <div className="row mb-4">
        <div className="col-md-3">
          <div className="card border-0 shadow-sm" style={{ borderLeft: '4px solid #6B8E23' }}>
            <div className="card-body"><small className="text-muted">Total Articles</small><h4 className="fw-bold mb-0" style={{ color: '#6B8E23' }}>{stats.total || 0}</h4></div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card border-0 shadow-sm" style={{ borderLeft: '4px solid #28A745' }}>
            <div className="card-body"><small className="text-muted">Published</small><h4 className="fw-bold mb-0" style={{ color: '#28A745' }}>{stats.published || 0}</h4></div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card border-0 shadow-sm" style={{ borderLeft: '4px solid #FF9800' }}>
            <div className="card-body"><small className="text-muted">Featured</small><h4 className="fw-bold mb-0" style={{ color: '#FF9800' }}>{stats.featured || 0}</h4></div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card border-0 shadow-sm" style={{ borderLeft: '4px solid #2196F3' }}>
            <div className="card-body"><small className="text-muted">Categories</small><h4 className="fw-bold mb-0" style={{ color: '#2196F3' }}>{stats.categories || 0}</h4></div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card border-0 shadow-sm mb-4">
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-4">
              <input type="text" className="form-control" placeholder="Search..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
            </div>
            <div className="col-md-3">
              <select className="form-select" value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)}>
                <option value="">All Categories</option>
                {categories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
              </select>
            </div>
            <div className="col-md-3">
              <select className="form-select" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
                <option value="">All Status</option>
                {statusOptions.map(status => <option key={status} value={status}>{status}</option>)}
              </select>
            </div>
            <div className="col-md-2">
              <button className="btn btn-outline-secondary w-100" onClick={() => { setSearchTerm(''); setFilterCategory(''); setFilterStatus(''); }}>Clear</button>
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="card border-0 shadow-sm">
        <div className="table-responsive">
          <table className="table table-hover mb-0">
            <thead style={{ backgroundColor: '#f8f9fa' }}>
              <tr>
                <th className="px-4 py-3">Title</th>
                <th className="py-3">Category</th>
                <th className="py-3">Author</th>
                <th className="py-3">Published</th>
                <th className="py-3">Status</th>
                <th className="py-3 text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="6" className="text-center py-5"><div className="spinner-border" style={{ color: '#6B8E23' }}></div></td></tr>
              ) : news.length === 0 ? (
                <tr><td colSpan="6" className="text-center py-5 text-muted">No news articles found</td></tr>
              ) : (
                news.map((item) => (
                  <tr key={item.id}>
                    <td className="px-4 py-3">
                      <div className="d-flex align-items-start">
                        <div>
                          <div className="fw-medium mb-1">{item.title}</div>
                          {item.featured && <span className="badge bg-warning text-dark small">Featured</span>}
                        </div>
                      </div>
                    </td>
                    <td className="py-3"><span className="badge" style={{ backgroundColor: '#6B8E23', color: 'white' }}>{item.category}</span></td>
                    <td className="py-3">{item.author}</td>
                    <td className="py-3">{item.published_date ? new Date(item.published_date).toLocaleDateString() : '—'}</td>
                    <td className="py-3">
                      <span className={`badge ${item.status === 'Published' ? 'bg-success' : item.status === 'Draft' ? 'bg-secondary' : 'bg-warning text-dark'}`}>
                        {item.status}
                      </span>
                    </td>
                    <td className="py-3 text-center">
                      <div className="btn-group btn-group-sm" role="group">
                        <button className="btn btn-outline-primary" onClick={() => handleEdit(item)}><i className="fas fa-edit"></i></button>
                        <button className="btn btn-outline-danger" onClick={() => handleDelete(item.id)}><i className="fas fa-trash"></i></button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} onClick={() => setShowModal(false)}>
          <div className="modal-dialog modal-xl modal-dialog-centered" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content" style={{ maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
              {/* Header with gradient - FIXED */}
              <div className="modal-header border-0" style={{ background: 'linear-gradient(135deg, #6B8E23 0%, #556B1F 100%)', flexShrink: 0 }}>
                <div className="d-flex align-items-center">
                  <div className="rounded-circle d-flex align-items-center justify-content-center me-3"
                    style={{ width: '48px', height: '48px', backgroundColor: 'rgba(255,255,255,0.2)' }}>
                    <span className="fs-3">📰</span>
                  </div>
                  <div>
                    <h5 className="modal-title text-white mb-0">{modalMode === 'create' ? 'Add News Article' : 'Edit News Article'}</h5>
                    <small className="text-white opacity-75">Fill in the details below</small>
                  </div>
                </div>
                <button type="button" className="btn-close btn-close-white" onClick={() => setShowModal(false)}></button>
              </div>
              <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
                {/* SCROLLABLE BODY */}
                <div className="modal-body" style={{ overflowY: 'auto', flex: 1 }}>
                  {/* Article Information Section */}
                  <div className="mb-4">
                    <h6 className="fw-bold mb-3 pb-2 border-bottom" style={{ color: '#6B8E23' }}>
                      <i className="fas fa-info-circle me-2"></i>Article Information
                    </h6>
                    <div className="row g-3">
                      <div className="col-12">
                        <label className="form-label fw-bold">Title *</label>
                        <input type="text" className="form-control form-control-lg" name="title" value={formData.title} onChange={handleInputChange} required placeholder="Enter article title" />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label fw-bold">Category *</label>
                        <select className="form-select form-select-lg" name="category" value={formData.category} onChange={handleInputChange} required>
                          <option value="">Select Category</option>
                          {categories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                        </select>
                      </div>
                      <div className="col-md-6">
                        <label className="form-label fw-bold">Author *</label>
                        <input type="text" className="form-control form-control-lg" name="author" value={formData.author} onChange={handleInputChange} required placeholder="Author name" />
                      </div>
                    </div>
                  </div>

                  {/* Content Section */}
                  <div className="mb-4">
                    <h6 className="fw-bold mb-3 pb-2 border-bottom" style={{ color: '#6B8E23' }}>
                      <i className="fas fa-file-alt me-2"></i>Content
                    </h6>
                    <div className="row g-3">
                      <div className="col-12">
                        <label className="form-label fw-bold">Excerpt (Summary) *</label>
                        <textarea className="form-control" name="excerpt" rows="3" value={formData.excerpt} onChange={handleInputChange} required placeholder="Brief summary shown in preview cards"></textarea>
                        <small className="text-muted">This will be displayed as the preview text (keep it concise)</small>
                      </div>
                      <div className="col-12">
                        <label className="form-label fw-bold">Full Article Content</label>
                        <textarea className="form-control" name="content" rows="5" value={formData.content} onChange={handleInputChange} placeholder="Complete article content for the detail page (optional)"></textarea>
                        <small className="text-muted">Full story for readers who click to read more</small>
                      </div>
                    </div>
                  </div>

                  {/* Media Upload Section */}
                  <div className="mb-4">
                    <h6 className="fw-bold mb-3 pb-2 border-bottom" style={{ color: '#6B8E23' }}>
                      <i className="fas fa-image me-2"></i>Media Upload
                    </h6>
                    <div className="row g-3">
                      <div className="col-12">
                        <label className="form-label fw-bold">Upload Image or Video</label>
                        <input
                          type="file"
                          className="form-control form-control-lg"
                          accept="image/*,video/*"
                          onChange={handleImageUpload}
                          disabled={uploading}
                        />
                        <small className="text-muted d-block mt-2">
                          <i className="fas fa-info-circle me-1"></i>
                          Supported: JPG, PNG, GIF, MP4, WebM | Max size: 10MB
                        </small>
                      </div>
                      {imagePreview && (
                        <div className="col-12">
                          <div className="card border shadow-sm">
                            <div className="card-body p-3">
                              <div className="d-flex align-items-center justify-content-between mb-2">
                                <span className="fw-bold" style={{ color: '#6B8E23' }}>
                                  <i className="fas fa-check-circle me-1"></i>Preview:
                                </span>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-danger"
                                  onClick={() => { setImagePreview(null); setFormData(prev => ({ ...prev, image_url: '' })); }}
                                >
                                  <i className="fas fa-trash me-1"></i>Remove
                                </button>
                              </div>
                              <div className="text-center">
                                <img src={imagePreview} alt="Preview" className="img-fluid rounded shadow-sm" style={{ maxHeight: '250px', objectFit: 'contain' }} />
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                      {uploading && (
                        <div className="col-12">
                          <div className="card border-0 bg-light">
                            <div className="card-body text-center py-4">
                              <div className="spinner-border" style={{ color: '#6B8E23' }} role="status">
                                <span className="visually-hidden">Uploading...</span>
                              </div>
                              <p className="text-muted mt-3 mb-0 fw-bold">Uploading media, please wait...</p>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Publishing Settings Section */}
                  <div className="mb-3">
                    <h6 className="fw-bold mb-3 pb-2 border-bottom" style={{ color: '#6B8E23' }}>
                      <i className="fas fa-cog me-2"></i>Publishing Settings
                    </h6>
                    <div className="row g-3">
                      <div className="col-md-3">
                        <label className="form-label fw-bold">Published Date *</label>
                        <input type="date" className="form-control form-control-lg" name="published_date" value={formData.published_date} onChange={handleInputChange} required />
                      </div>
                      <div className="col-md-3">
                        <label className="form-label fw-bold">Display Order</label>
                        <input
                          type="number"
                          className="form-control form-control-lg"
                          name="display_order"
                          value={formData.display_order}
                          onChange={handleInputChange}
                          placeholder="0"
                        />
                        <small className="text-muted">Lower numbers appear first (1=first, 100=last)</small>
                      </div>
                      <div className="col-md-3">
                        <label className="form-label fw-bold">Status *</label>
                        <select className="form-select form-select-lg" name="status" value={formData.status} onChange={handleInputChange} required>
                          {statusOptions.map(status => <option key={status} value={status}>{status}</option>)}
                        </select>
                      </div>
                      <div className="col-md-3">
                        <label className="form-label fw-bold d-block">Featured Article</label>
                        <div className="card border-0 bg-light h-100 d-flex align-items-center justify-content-center" style={{ minHeight: '58px' }}>
                          <div className="form-check form-switch">
                            <input className="form-check-input" type="checkbox" name="featured" checked={formData.featured} onChange={handleInputChange} id="featuredSwitch" style={{ width: '3em', height: '1.5em' }} />
                            <label className="form-check-label fw-bold ms-2" htmlFor="featuredSwitch">
                              <i className="fas fa-star me-1" style={{ color: formData.featured ? '#FFC107' : '#ccc' }}></i>
                              {formData.featured ? 'Featured' : 'Not Featured'}
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                {/* FIXED FOOTER with visible buttons */}
                <div className="modal-footer border-top bg-light" style={{ flexShrink: 0, padding: '1rem 1.5rem' }}>
                  <button type="button" className="btn btn-lg btn-secondary px-4" onClick={() => setShowModal(false)}>
                    <i className="fas fa-times me-2"></i>Cancel
                  </button>
                  <button type="submit" className="btn btn-lg text-white px-5 fw-bold shadow" style={{ backgroundColor: '#6B8E23' }} disabled={uploading}>
                    <i className={`fas fa-${modalMode === 'create' ? 'plus-circle' : 'save'} me-2`}></i>
                    {modalMode === 'create' ? 'Create Article' : 'Update Article'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NewsManagement;
